// src/config/credits.ts
var CREDIT_CENTS_PER_UNIT = {
  watch: 5,
  create: 10
};
var CREDITS_PER_DOLLAR = {
  watch: 20,
  create: 10
};
function defaultPurchaseCredits(pool) {
  return CREDITS_PER_DOLLAR[pool];
}
function priceCentsForCredits(pool, credits) {
  return Math.max(1, credits) * CREDIT_CENTS_PER_UNIT[pool];
}
function formatCreditPrice(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// src/config/chuzi-ai-prompts.ts
var CHUZI_AI_PROMPT_STEPS = [];
var CHUZI_AI_GENERATOR_VIBES = [
  "absurdist",
  "action",
  "adventure",
  "afrofuturism",
  "biopunk",
  "cli-fi",
  "comedy",
  "cozy",
  "cyberpunk",
  "dieselpunk",
  "drama",
  "dystopian",
  "espionage",
  "fantasy",
  "gothic",
  "grimdark",
  "historical",
  "horror",
  "isekai",
  "magical-realism",
  "mystery",
  "mythology",
  "noir",
  "post-apocalyptic",
  "psychological",
  "romance",
  "satire",
  "sci-fi",
  "slice-of-life",
  "solarpunk",
  "space-opera",
  "steampunk",
  "supernatural",
  "surreal",
  "thriller",
  "western",
  "wuxia"
];
var AUDIENCE_AGE_OPTIONS = [
  { id: "4+", label: "Ages 4+", description: "Simple words, short sentences, gentle themes." },
  { id: "7+", label: "Ages 7+", description: "Easy reading, mild peril, playful humor." },
  { id: "10+", label: "Ages 10+", description: "Grade-school vocabulary, some suspense allowed." },
  { id: "13+", label: "Ages 13+", description: "Teen-level language, moderate complexity." },
  { id: "17+", label: "Ages 17+", description: "Adult vocabulary and mature themes." }
];
var AUDIENCE_AGE_IDS = AUDIENCE_AGE_OPTIONS.map((o) => o.id);
var CHUZI_AI_VIBE_LABELS = {
  "absurdist": "Absurdist",
  "action": "Action",
  "adventure": "Adventure",
  "afrofuturism": "Afrofuturism",
  "biopunk": "Biopunk",
  "cli-fi": "Cli-Fi",
  "comedy": "Comedy",
  "cozy": "Cozy",
  "cyberpunk": "Cyberpunk",
  "dieselpunk": "Dieselpunk",
  "drama": "Drama",
  "dystopian": "Dystopian",
  "espionage": "Espionage",
  "fantasy": "Fantasy",
  "gothic": "Gothic",
  "grimdark": "Grimdark",
  "historical": "Historical",
  "horror": "Horror",
  "isekai": "Isekai",
  "magical-realism": "Magical Realism",
  "mystery": "Mystery",
  "mythology": "Mythology",
  "noir": "Noir",
  "post-apocalyptic": "Post-Apocalyptic",
  "psychological": "Psychological",
  "romance": "Romance",
  "satire": "Satire",
  "sci-fi": "Sci-Fi",
  "slice-of-life": "Slice of Life",
  "solarpunk": "Solarpunk",
  "space-opera": "Space Opera",
  "steampunk": "Steampunk",
  "supernatural": "Supernatural",
  "surreal": "Surreal",
  "thriller": "Thriller",
  "western": "Western",
  "wuxia": "Wuxia"
};

// src/config/scene-text-fonts.ts
var SCENE_TEXT_FONT_DEFAULT_ID = "Playfair Display";
var SCENE_TEXT_ARROW_SAFE_INSET = {
  top: "5%",
  left: "5%",
  right: "20%",
  bottom: "22%"
};
var SCENE_TEXT_FONTS = [
  { id: "inherit", label: "Site Default", family: "inherit", googleSpec: "", mood: "neutral system" },
  { id: "Playfair Display", label: "Playfair Display", family: '"Playfair Display", Georgia, serif', googleSpec: "Playfair+Display:wght@400;700", mood: "literary drama" },
  { id: "Cinzel", label: "Cinzel", family: "Cinzel, Georgia, serif", googleSpec: "Cinzel:wght@400;700", mood: "epic classical" },
  { id: "Cormorant Garamond", label: "Cormorant Garamond", family: '"Cormorant Garamond", Georgia, serif', googleSpec: "Cormorant+Garamond:wght@400;700", mood: "elegant romance" },
  { id: "EB Garamond", label: "EB Garamond", family: '"EB Garamond", Georgia, serif', googleSpec: "EB+Garamond:wght@400;700", mood: "old-world book" },
  { id: "Libre Baskerville", label: "Libre Baskerville", family: '"Libre Baskerville", Georgia, serif', googleSpec: "Libre+Baskerville:wght@400;700", mood: "vintage mystery" },
  { id: "Merriweather", label: "Merriweather", family: "Merriweather, Georgia, serif", googleSpec: "Merriweather:wght@400;700", mood: "warm readable serif" },
  { id: "Lora", label: "Lora", family: "Lora, Georgia, serif", googleSpec: "Lora:wght@400;700", mood: "soft literary" },
  { id: "Spectral", label: "Spectral", family: "Spectral, Georgia, serif", googleSpec: "Spectral:wght@400;700", mood: "editorial serif" },
  { id: "Crimson Text", label: "Crimson Text", family: '"Crimson Text", Georgia, serif', googleSpec: "Crimson+Text:wght@400;700", mood: "poetic classic" },
  { id: "Georgia", label: "Georgia", family: "Georgia, serif", googleSpec: "", mood: "traditional serif" },
  { id: "Times New Roman", label: "Times New Roman", family: '"Times New Roman", Times, serif', googleSpec: "", mood: "formal print" },
  { id: "Bebas Neue", label: "Bebas Neue", family: '"Bebas Neue", Impact, sans-serif', googleSpec: "Bebas+Neue", mood: "bold poster" },
  { id: "Anton", label: "Anton", family: "Anton, Impact, sans-serif", googleSpec: "Anton", mood: "loud headline" },
  { id: "Oswald", label: "Oswald", family: "Oswald, Arial, sans-serif", googleSpec: "Oswald:wght@400;700", mood: "condensed tension" },
  { id: "Barlow Condensed", label: "Barlow Condensed", family: '"Barlow Condensed", Arial, sans-serif', googleSpec: "Barlow+Condensed:wght@400;700", mood: "noir narrow" },
  { id: "Montserrat", label: "Montserrat", family: "Montserrat, Arial, sans-serif", googleSpec: "Montserrat:wght@400;700", mood: "modern clean" },
  { id: "Raleway", label: "Raleway", family: "Raleway, Arial, sans-serif", googleSpec: "Raleway:wght@400;700", mood: "airy minimal" },
  { id: "Poppins", label: "Poppins", family: "Poppins, Arial, sans-serif", googleSpec: "Poppins:wght@400;700", mood: "friendly sans" },
  { id: "Josefin Sans", label: "Josefin Sans", family: '"Josefin Sans", Arial, sans-serif', googleSpec: "Josefin+Sans:wght@400;700", mood: "retro art deco" },
  { id: "Roboto", label: "Roboto", family: "Roboto, Arial, sans-serif", googleSpec: "Roboto:wght@400;700", mood: "neutral sans" },
  { id: "Arial", label: "Arial", family: "Arial, Helvetica, sans-serif", googleSpec: "", mood: "plain sans" },
  { id: "Helvetica", label: "Helvetica", family: "Helvetica, Arial, sans-serif", googleSpec: "", mood: "swiss sans" },
  { id: "Verdana", label: "Verdana", family: "Verdana, Geneva, sans-serif", googleSpec: "", mood: "screen sans" },
  { id: "Abril Fatface", label: "Abril Fatface", family: '"Abril Fatface", Georgia, serif', googleSpec: "Abril+Fatface", mood: "luxury display" },
  { id: "Alfa Slab One", label: "Alfa Slab One", family: '"Alfa Slab One", Georgia, serif', googleSpec: "Alfa+Slab+One", mood: "western slab" },
  { id: "Bungee", label: "Bungee", family: "Bungee, Impact, sans-serif", googleSpec: "Bungee", mood: "urban shout" },
  { id: "Righteous", label: "Righteous", family: "Righteous, Impact, sans-serif", googleSpec: "Righteous", mood: "retro funk" },
  { id: "Lobster", label: "Lobster", family: "Lobster, cursive", googleSpec: "Lobster", mood: "playful script" },
  { id: "Pacifico", label: "Pacifico", family: "Pacifico, cursive", googleSpec: "Pacifico", mood: "surf casual" },
  { id: "Dancing Script", label: "Dancing Script", family: '"Dancing Script", cursive', googleSpec: "Dancing+Script:wght@400;700", mood: "romantic hand" },
  { id: "Caveat", label: "Caveat", family: "Caveat, cursive", googleSpec: "Caveat:wght@400;700", mood: "notebook scribble" },
  { id: "Sacramento", label: "Sacramento", family: "Sacramento, cursive", googleSpec: "Sacramento", mood: "elegant script" },
  { id: "Satisfy", label: "Satisfy", family: "Satisfy, cursive", googleSpec: "Satisfy", mood: "loose signature" },
  { id: "Permanent Marker", label: "Permanent Marker", family: '"Permanent Marker", cursive', googleSpec: "Permanent+Marker", mood: "graffiti marker" },
  { id: "Special Elite", label: "Special Elite", family: '"Special Elite", monospace', googleSpec: "Special+Elite", mood: "typewriter noir" },
  { id: "Courier Prime", label: "Courier Prime", family: '"Courier Prime", monospace', googleSpec: "Courier+Prime:wght@400;700", mood: "manuscript mono" },
  { id: "Courier New", label: "Courier New", family: '"Courier New", Courier, monospace', googleSpec: "", mood: "terminal mono" },
  { id: "Space Mono", label: "Space Mono", family: '"Space Mono", monospace', googleSpec: "Space+Mono:wght@400;700", mood: "sci-fi mono" },
  { id: "VT323", label: "VT323", family: "VT323, monospace", googleSpec: "VT323", mood: "arcade terminal" },
  { id: "Press Start 2P", label: "Press Start 2P", family: '"Press Start 2P", monospace', googleSpec: "Press+Start+2P", mood: "8-bit game" },
  { id: "Creepster", label: "Creepster", family: "Creepster, fantasy", googleSpec: "Creepster", mood: "horror drip" },
  { id: "UnifrakturMaguntia", label: "UnifrakturMaguntia", family: "UnifrakturMaguntia, fantasy", googleSpec: "UnifrakturMaguntia", mood: "gothic blackletter" },
  { id: "Rubik Glitch", label: "Rubik Glitch", family: '"Rubik Glitch", fantasy', googleSpec: "Rubik+Glitch", mood: "glitch chaos" },
  { id: "Rubik Wet Paint", label: "Rubik Wet Paint", family: '"Rubik Wet Paint", fantasy', googleSpec: "Rubik+Wet+Paint", mood: "melting wild" },
  { id: "Titan One", label: "Titan One", family: '"Titan One", fantasy', googleSpec: "Titan+One", mood: "cartoon punch" },
  { id: "Bangers", label: "Bangers", family: "Bangers, fantasy", googleSpec: "Bangers", mood: "comic blast" },
  { id: "Fredericka the Great", label: "Fredericka the Great", family: '"Fredericka the Great", fantasy', googleSpec: "Fredericka+the+Great", mood: "ornate fantasy" },
  { id: "Eater", label: "Eater", family: "Eater, fantasy", googleSpec: "Eater", mood: "creepy bite" },
  { id: "Nosifer", label: "Nosifer", family: "Nosifer, fantasy", googleSpec: "Nosifer", mood: "blood drip" },
  { id: "Metal Mania", label: "Metal Mania", family: '"Metal Mania", fantasy', googleSpec: "Metal+Mania", mood: "heavy metal" },
  { id: "Rye", label: "Rye", family: "Rye, fantasy", googleSpec: "Rye", mood: "wanted poster" },
  { id: "Pirata One", label: "Pirata One", family: '"Pirata One", fantasy', googleSpec: "Pirata+One", mood: "pirate adventure" },
  { id: "Fascinate Inline", label: "Fascinate Inline", family: '"Fascinate Inline", fantasy', googleSpec: "Fascinate+Inline", mood: "disco inline" }
];
var FONT_BY_ID = new Map(SCENE_TEXT_FONTS.map((font) => [font.id.toLowerCase(), font]));
var FONT_BY_FAMILY = new Map(
  SCENE_TEXT_FONTS.flatMap((font) => [
    [font.family.toLowerCase(), font],
    [font.id.toLowerCase(), font]
  ])
);
var CHUZI_AI_SCENE_FONT_IDS = SCENE_TEXT_FONTS.filter((font) => font.id !== "inherit").map((font) => font.id);
function chuziAiSceneFontPromptList() {
  return CHUZI_AI_SCENE_FONT_IDS.join("|");
}
function resolveSceneTextFont(stored) {
  const raw = (stored ?? "").trim();
  if (!raw || raw === "inherit") {
    return FONT_BY_ID.get("inherit") ?? SCENE_TEXT_FONTS[0];
  }
  const byId = FONT_BY_ID.get(raw.toLowerCase());
  if (byId) return byId;
  const byFamily = FONT_BY_FAMILY.get(raw.toLowerCase());
  if (byFamily) return byFamily;
  const legacy = SCENE_TEXT_FONTS.find(
    (font) => raw.toLowerCase().includes(font.id.toLowerCase())
  );
  if (legacy) return legacy;
  return {
    id: raw,
    label: raw,
    family: raw,
    googleSpec: "",
    mood: "custom"
  };
}
function sceneTextFontCss(stored) {
  return resolveSceneTextFont(stored).family;
}
function sceneTextFontOptions() {
  return SCENE_TEXT_FONTS.map((font) => ({
    value: font.id,
    label: font.label,
    family: font.family
  }));
}
function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}
function buildSceneTextFontStylesheetUrls() {
  const specs = SCENE_TEXT_FONTS.map((font) => font.googleSpec).filter(Boolean);
  return chunk(specs, 12).map((group) => {
    const query = group.map((spec) => `family=${spec}`).join("&");
    return `https://fonts.googleapis.com/css2?${query}&display=swap`;
  });
}
function ensureSceneTextFontsLoaded(doc = document) {
  const head = doc.head;
  buildSceneTextFontStylesheetUrls().forEach((href, index) => {
    const id = `chuzi-scene-fonts-${index}`;
    if (head.querySelector(`link#${id}`)) return;
    const link = doc.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = href;
    head.appendChild(link);
  });
}

// src/config/index.ts
var REALM_IDS = ["cosmos", "wilds"];
var SUPPORTED_LOCALES = [
  "en",
  "es",
  "fr",
  "de",
  "pt"
];
var LOCALE_LABELS = {
  en: "English",
  es: "Espa\xF1ol",
  fr: "Fran\xE7ais",
  de: "Deutsch",
  pt: "Portugu\xEAs"
};
var DEFAULT_LOCALE = "en";
var runtimeRealmLexiconOverrides = {};
function getRealmLexiconOverrides() {
  return {
    cosmos: runtimeRealmLexiconOverrides.cosmos ? { ...runtimeRealmLexiconOverrides.cosmos } : void 0,
    wilds: runtimeRealmLexiconOverrides.wilds ? { ...runtimeRealmLexiconOverrides.wilds } : void 0
  };
}
function setRealmLexiconOverrides(overrides) {
  runtimeRealmLexiconOverrides = {
    cosmos: overrides.cosmos ? { ...overrides.cosmos } : void 0,
    wilds: overrides.wilds ? { ...overrides.wilds } : void 0
  };
}
function isSupportedLocale(value) {
  return typeof value === "string" && SUPPORTED_LOCALES.includes(value);
}
function normalizeLocale(value) {
  if (!value) return null;
  const lower = value.toLowerCase().trim();
  if (isSupportedLocale(lower)) return lower;
  const base = lower.split(/[-_]/, 1)[0];
  return isSupportedLocale(base) ? base : null;
}
function matchAcceptLanguage(accept) {
  if (!accept) return null;
  const tags = accept.split(",").map((part) => {
    const [tag, ...params] = part.trim().split(";");
    const qParam = params.find((p) => p.trim().startsWith("q="));
    const q = qParam ? parseFloat(qParam.split("=")[1]) || 0 : 1;
    return { tag: tag.trim(), q };
  }).filter((c) => c.tag && c.tag !== "*").sort((a, b) => b.q - a.q);
  for (const c of tags) {
    const normalized = normalizeLocale(c.tag);
    if (normalized) return normalized;
  }
  return null;
}
var CONTENT_RATING_IDS = [
  "G",
  "PG",
  "PG-13",
  "R",
  "NC-17"
];
var CONTENT_RATINGS = {
  G: {
    id: "G",
    label: "G",
    description: "General Audiences \u2014 all ages admitted."
  },
  PG: {
    id: "PG",
    label: "PG",
    description: "Parental Guidance Suggested \u2014 some material may not be suitable for children."
  },
  "PG-13": {
    id: "PG-13",
    label: "PG-13",
    description: "Parents Strongly Cautioned \u2014 some material may be inappropriate for children under 13."
  },
  R: {
    id: "R",
    label: "R",
    description: "Restricted \u2014 under 17 requires accompanying parent or adult guardian."
  },
  "NC-17": {
    id: "NC-17",
    label: "NC-17",
    description: "Adults Only \u2014 no one 17 and under admitted."
  }
};
function isContentRating(value) {
  return typeof value === "string" && CONTENT_RATING_IDS.includes(value);
}
var REALMS = {
  cosmos: {
    label: "CHUZI COSMOS",
    short_label: "Cosmos",
    lexicon: {
      genre: "Galaxy",
      story: "Star System",
      scene: "Star",
      scene_choice: "Choice",
      node: "Signal",
      node_type_choice: "Choice",
      node_type_media: "Transmission",
      node_type_go_to_scene: "Warp",
      player: "Voyager",
      library: "Star Chart",
      library_open: "Open Star Chart",
      library_close: "Close Star Chart",
      editor: "Mission Control",
      publish: "Launch",
      experience_realm_idea: "Outer Space",
      the_end: "The End",
      // deprecated — no longer generated
      media_pool: "Cargo Bay",
      scene_graph: "Star Chart",
      watch_path: "Your path",
      untitled_story: "Untitled Star System",
      untitled_scene: "Untitled Star",
      unknown_genre: "Unknown Galaxy",
      scenes_count: "stars",
      choices_count: "choices",
      drafts_published: "Drafts + Launched",
      published_ver: "Launched ver.",
      draft: "Draft",
      no_scenes_yet: "No stars in this chart yet.",
      no_path_yet: "No path to show yet.",
      menu_empty_drafts: "No star systems yet. Chart one to begin.",
      menu_empty_published: "No launched systems in the chart.",
      menu_published_heading: "Launched constellations",
      menu_create_story: "New star system",
      menu_create_film: "Create film",
      film_title_placeholder: "Star system title",
      select_genre: "Select galaxy",
      genre_field_aria: "Galaxy",
      delete_story_verb: "Delete star system",
      title_scene_default: "Launch",
      choices_made_stat: "choices made",
      content_rating: "Audience Class",
      content_rating_field_aria: "Audience class",
      select_content_rating: "Select audience class",
      unrated: "Unclassified",
      viewer_credits: "Fuel Cells",
      creator_credits: "Stardust",
      editor_media_upload: "Upload Transmission",
      editor_media_preview: "Transmission Preview",
      editor_alt_media: "Effects",
      editor_choices: "Choices",
      editor_scene_settings: "Star Settings",
      editor_scene_type_label: "Star",
      editor_title_scene: "Start",
      editor_film_title: "Star System Title",
      editor_film_title_hint: "Click to rename this star system",
      editor_ending_scene: "End",
      editor_play: "Engage",
      editor_pause: "Hold",
      editor_prev_frame: "Previous Frame",
      editor_next_frame: "Next Frame",
      editor_seek: "Seek",
      editor_no_media: "No transmission assigned",
      editor_uploading: "Transmitting\u2026",
      editor_drop_media: "Drop transmission here",
      editor_scene_type: "Star Type",
      editor_save: "Lock In",
      editor_cancel: "Abort",
      editor_create_scene: "Chart Star",
      editor_create_scene_plain: "Create New Scene",
      editor_create_scene_before: "Insert New Star Before",
      editor_create_scene_after: "Insert New Star After",
      editor_create_scene_ahead: "Create New Star",
      editor_create_next_scene: "Chart Next Star",
      editor_create_scene_cost: "1 Stardust",
      editor_delete_scene_cta: "Delete Star",
      editor_delete_scene_confirm: "Click again to confirm",
      editor_scene_locked_delete: "Start star cannot be deleted.",
      editor_scene_color: "Star Hue",
      viewer_continue: "Continue Signal",
      chuzi_ai_name: "Chuzi AI",
      chuzi_ai_create_with: "Create with Chuzi AI",
      chuzi_ai_create_manual: "Enter title manually",
      chuzi_ai_intro: "Answer a few story prompts and Chuzi AI will chart your short story.",
      chuzi_ai_step_of: "Step",
      chuzi_ai_back: "Back",
      chuzi_ai_next: "Next",
      chuzi_ai_scenes_label: "scenes",
      chuzi_ai_creating: "Creating\u2026",
      chuzi_ai_generate_failed: "Story generation failed.",
      chuzi_ai_build_failed: "Failed to build story.",
      chuzi_ai_generate: "Generate scene",
      chuzi_ai_generating: "Generating\u2026",
      chuzi_ai_token_in: "Tokens in",
      chuzi_ai_token_out: "Tokens out",
      chuzi_ai_credits_cost: "AI credits",
      chuzi_ai_scene_credits: "Scene credits",
      chuzi_ai_total_cost: "Total create credits",
      chuzi_ai_insufficient: "Not enough create credits for this generation.",
      chuzi_ai_apply: "Build story",
      chuzi_ai_applying: "Building scenes\u2026",
      chuzi_ai_scene_create: "Create scene with Chuzi AI",
      chuzi_ai_scene_overwrite: "Rewrite scene with Chuzi AI",
      chuzi_ai_scene_brief: "Scene direction",
      chuzi_ai_scene_brief_hint: "Describe what should happen in this scene. Chuzi AI reads your existing story for characters, tone, and style.",
      chuzi_ai_scene_context_note: "Uses your story\u2019s existing scenes, typography, and art direction as context.",
      chuzi_ai_scene_overwrite_confirm: "I understand this replaces this scene\u2019s text and styling.",
      chuzi_ai_scene_apply_create: "Create scene",
      chuzi_ai_scene_apply_overwrite: "Replace scene",
      chuzi_ai_scene_continue_default: "Continue the story with the next scene. Leave the narrative open-ended.",
      chuzi_ai_scene_continue_vibes: "Stay in the same tone and genre ({vibes}).",
      chuzi_ai_scene_continue_characters: "Keep roughly {count} characters in play.",
      chuzi_ai_scene_continue_hook: "Write the next scene continuing from where the story left off. Do not end or resolve the story.",
      editor_scene_nav: "Scene navigation",
      editor_scene_title_hint: "Click to rename this star",
      chuzi_ai_step_protagonist_hint: "Who is your main character? Name, age, personality, and what makes them interesting.",
      chuzi_ai_step_setting: "Setting",
      chuzi_ai_step_setting_hint: "Where and when does the story begin? Describe the world and their everyday life.",
      chuzi_ai_step_conflict: "Conflict",
      chuzi_ai_step_conflict_hint: "What event or problem kicks the story into motion?",
      chuzi_ai_step_journey: "Journey",
      chuzi_ai_step_journey_hint: "What challenges, turning points, climax, and resolution shape the story?",
      chuzi_ai_step_tone: "Tone & mood",
      chuzi_ai_step_tone_hint: "Genre, mood, and atmosphere (e.g. bittersweet sci-fi, cozy mystery, epic fantasy).",
      chuzi_ai_monomyth_hero: "Hero\u2019s Journey: the protagonist",
      chuzi_ai_monomyth_world: "Monomyth: ordinary world",
      chuzi_ai_monomyth_you: "Story Circle: zone of comfort",
      chuzi_ai_monomyth_need: "Story Circle: something isn\u2019t right",
      chuzi_ai_monomyth_go: "Monomyth: crossing the first threshold",
      chuzi_ai_monomyth_search: "Monomyth: road of trials",
      chuzi_ai_monomyth_find: "Monomyth: the ultimate boon",
      chuzi_ai_monomyth_take: "Monomyth: atonement with the father",
      chuzi_ai_monomyth_return: "Monomyth: crossing the return threshold",
      chuzi_ai_monomyth_change: "Monomyth: freedom to live",
      cosmos_cluster_published: "Broadcast Nebula",
      cosmos_cluster_unpublished: "Shipyard Nebula",
      cosmos_cluster_limited: "Relay Nebula",
      cosmos_cluster_jettisoned: "Jettison Nebula",
      jettison_unjettison: "Recover",
      jettison_promote: "Return to Shipyard",
      jettison_purge_story: "Purge",
      jettison_purge_all: "Purge All",
      jettison_days_remaining: "{days}d remaining",
      nav_bar_empty_nebula: "No star systems",
      nebula_chuzi: "CHUZI!",
      published_version_label: "Version",
      system_story_create: "New reserved story",
      publish_tier_private: "Private",
      publish_tier_limited: "Limited access",
      publish_tier_worldwide: "Worldwide",
      hud_unpublish_control: "Jettison",
      hud_retract_control: "Retract",
      unpublish_confirm_hint: "Tap again to confirm",
      create_publish_hint: "Add ratings, genre & coverbox when you broadcast.",
      generator_create_upload: "Create / Upload Transmission",
      generator_generate: "Generate with Chuzi AI",
      generator_vibes_prompt: "Choose your vibes",
      generator_vibes_required: "Pick at least one vibe.",
      generator_audience_prompt: "Who\u2019s your audience?",
      generator_audience_placeholder: "Select reading level",
      generator_characters_prompt: "How many characters?",
      generator_details_prompt: "Any details to add?",
      generator_details_placeholder: "Describe your universe\u2026",
      generator_record_cta: "Speak your universe",
      editor_ai_generate: "Generate Transmission",
      editor_ai_generating: "Transmitting Signal\u2026",
      editor_ai_mood_question: "What energy should this star radiate?",
      editor_ai_style_question: "What vision style for this transmission?",
      editor_ai_prompt_question: "Describe this star\u2019s landscape",
      editor_ai_accept: "Lock Transmission",
      editor_ai_reject: "Reject Signal",
      editor_ai_retry: "Retransmit",
      editor_ai_feedback_prompt: "How should we adjust the signal?",
      editor_ai_credit_cost: "1 Stardust per signal",
      spending_modal_title: "Refuel Your Mission",
      spending_modal_body: "You need more credits to continue. Choose how many to add.",
      spending_modal_no_payment: "Add a payment method to purchase credits.",
      spending_modal_credits_label: "Credits to add",
      spending_modal_price_label: "Total",
      spending_modal_admin_note: "Admin \u2014 credits are granted free of charge.",
      spending_modal_purchase: "Purchase Credits",
      spending_modal_grant: "Grant Credits",
      spending_modal_add_card: "Add Payment Method",
      spending_modal_card_last_four: "Card last four digits",
      spending_modal_card_label: "Card label (optional)",
      spending_modal_processing: "Processing\u2026",
      spending_modal_success: "Credits added.",
      spending_modal_error: "Could not complete purchase. Try again.",
      spending_modal_cancel: "Not now",
      spending_insufficient_credits: "Not enough credits. Purchase more to continue.",
      spending_add_credits_continue: "Add credits to continue",
      editor_ai_generating_scene: "Star forming\u2026",
      editor_ai_style_badge: "Signal Style",
      editor_ai_failed: "Signal lost. Try retransmitting.",
      editor_media_attached: "This star already has a transmission locked in.",
      editor_remove_media: "Remove Transmission",
      editor_media_question: "What's this scene's media?",
      editor_media_type_text: "Text",
      editor_media_type_imagery: "Imagery",
      editor_media_type_film: "Film",
      editor_media_preview_cta: "Preview",
      editor_media_preview_disabled: "Add text to enable preview.",
      editor_text_toolbar_label: "Text / Video",
      editor_text_font_label: "Font",
      editor_text_color_label: "Text Color",
      editor_text_background_label: "Background",
      editor_text_font_size_label: "Font Size",
      editor_text_placeholder: "Start writing scene media...",
      editor_line_add: "Add Text Block",
      editor_add_text: "Add Text",
      editor_add_video: "Add Video",
      editor_text_lines_section: "Transmission Lines",
      editor_line_editor_label: "Editor",
      editor_line_number: "Line",
      editor_select_line_hint: "Select a transmission line to edit",
      editor_select_effect_hint: "Select an effect to edit",
      editor_select_choice_hint: "Select a choice to edit",
      editor_line_timestamp: "Appears at",
      editor_line_fade_in: "Fade In",
      editor_line_fade_out: "Fade Out",
      editor_line_duration: "Duration",
      editor_line_persist: "Stay on Screen",
      editor_effects_section: "Effects",
      editor_effects_add: "Add sounds, effects, etc",
      editor_add_sound_music: "Add Sound / Music",
      editor_add_fx: "Add FX",
      editor_node_list_section: "Transmission Layers",
      editor_exit_choice_label: "Go To The End",
      editor_bg_layer_label: "Background",
      editor_remove_layer: "Remove",
      editor_new_node: "New\u2026",
      editor_new_text: "Lines of Text",
      editor_new_choice: "Choice",
      editor_new_image: "Image",
      editor_new_video: "Video",
      editor_new_music: "Music",
      editor_new_sound: "Sound Effect",
      editor_new_logic: "Logic",
      editor_new_credits: "Chosen by\u2026",
      editor_credits_layer: "Credits",
      editor_logic_mode_set: "Set Variable",
      editor_logic_mode_evaluate: "Evaluate",
      editor_logic_variable_label: "Variable",
      editor_logic_value_label: "Value",
      editor_logic_operator_label: "Condition",
      editor_logic_then_layers: "Then activate layers",
      editor_logic_new_variable: "New variable\u2026",
      editor_insert_star_menu: "Insert New Star\u2026",
      editor_select_node_hint: "Select a layer to edit",
      editor_effect_describe_prompt: "Describe what you want in detail",
      editor_effect_share_toggle: "Allow others to use this?",
      editor_effect_share_yes: "Yes, share with others",
      editor_effect_share_no: "No, keep it private",
      editor_effect_generating: "Generating\u2026",
      editor_effect_fade_in_sound: "Fade In Sound",
      editor_effect_fade_out_sound: "Fade Out Sound",
      editor_effect_fade_in_visual: "Fade In",
      editor_effect_fade_out_visual: "Fade Out",
      editor_timeline_label: "Timeline",
      editor_preview_label: "Preview",
      editor_preview_empty_hint: "Begin transmission",
      editor_preview_interact_hint: "Preview \u2014 drag transmission to position, resize from corner",
      editor_preview_play: "Play",
      editor_preview_pause: "Pause",
      editor_media_placeholder_imagery: "Imagery authoring is coming soon.",
      editor_media_placeholder_film: "Film authoring is coming soon.",
      editor_goto_scene: "Goto Star",
      editor_scene_duration: "Transmission Duration",
      editor_add_choice: "Add Choice",
      editor_arrow_tool: "Place Arrow",
      editor_goto_nullified: "Disabled \u2014 choices override goto",
      editor_remove_choice: "Remove Choice",
      editor_choice_target: "Destination Star",
      editor_choices_empty_hint: "Set a goto star or add a choice to branch the story.",
      editor_goto_none: "None",
      editor_choice_label: "Choice",
      editor_choice_display_label: "Signal Label",
      editor_choice_display_label_hint: "Leave blank to hide the label beneath the arrow. Lexicon keys like viewer_continue resolve to realm text.",
      editor_choice_display_label_empty: "No label",
      editor_choice_display_label_preview: "Preview",
      editor_choice_exit_target_hint: "Terminal exit \u2014 ends the story for viewers.",
      editor_flow_timeline: "Story Flow",
      editor_flow_goto_path: "Default Goto",
      editor_flow_add_choice_here: "Add Choice Here",
      editor_flow_choice_branch: "Branch",
      editor_flow_content_ref: "Content",
      editor_scene_duration_unit: "seconds",
      editor_scene_duration_override_hint: "Overrides transmission length. Signal runtime: {duration}s",
      editor_arrow_scale: "Span",
      editor_arrow_rotation: "Bearing",
      editor_arrow_rotate_hint: "Drag the beacon above the arrow in preview to rotate",
      constellation_no_coverbox: "Uncharted",
      hud_edit_control: "Engage",
      hud_delete_control: "Jettison",
      hud_publish_control: "Launch",
      hud_launch_control: "Launch",
      editor_publish_story: "Promote",
      viewer_loading: "Loading transmission\u2026",
      viewer_preparing: "Spooling media\u2026",
      viewer_close: "Exit",
      viewer_complete: "End Transmission",
      viewer_stage_label: "Viewport",
      viewer_choose_path: "Choose",
      viewer_scrub_label: "Playback position",
      create_button: "CREATE",
      publish_form_heading: "Launch readiness",
      tags_label: "Signal Tags",
      tags_placeholder: "Search signal tags\u2026",
      tags_empty: "No signal tags yet",
      tags_required: "At least one signal tag is required.",
      tags_clear: "Clear tag filter",
      all_nav_search_placeholder: "Scan transmissions\u2026",
      all_nav_sort_label: "Order by",
      all_nav_empty_nebula: "No transmissions",
      catalog_order_label: "Sort by",
      catalog_order_newest: "Newest Transmission",
      catalog_order_oldest: "Oldest Transmission",
      catalog_order_most_viewed: "Most viewed",
      catalog_order_least_viewed: "Least viewed",
      error_rate_limited: "Signal congestion. Wait a moment and try again.",
      story_picker_label: "Constellations",
      created_by: "Directed by",
      chosen_by: "Chosen by",
      story_metadata_heading: "Star system details",
      story_metadata_save: "Save",
      constellation_title_edit_hint: "Edit star system title and details",
      director_profile_title: "Director profile",
      director_profile_name_label: "Name",
      director_profile_realm_label: "Experience realm",
      director_profile_image_label: "Image",
      director_profile_image_hint: "Realm-themed seal \u2014 switching the realm above swaps the seal. Custom uploads land with the V1 avatar endpoints.",
      director_profile_tab_profile: "Profile",
      director_profile_tab_experience_mapping: "Experience Mapping",
      director_profile_admin_only: "Admin only",
      director_profile_cancel: "Cancel",
      director_profile_close: "Close",
      director_profile_save: "Save",
      director_profile_saving: "Saving\u2026",
      director_profile_name_required: "Name is required.",
      director_profile_username_locked_hint: "Call sign locked at enlistment \u2014 cannot be reassigned.",
      director_profile_save_failed: "Save failed",
      experience_mapping_loading: "Loading experience mapping\u2026",
      experience_mapping_empty: "No template hierarchy available yet.",
      experience_mapping_retry: "Retry",
      experience_mapping_story_label: "Star System",
      experience_mapping_scene_label: "Star",
      experience_mapping_action_label: "Action",
      experience_mapping_field_title: "Title",
      experience_mapping_field_description: "Description",
      experience_mapping_field_genre: "Genre",
      experience_mapping_field_content_rating: "Content rating",
      experience_mapping_field_color: "Color",
      experience_mapping_field_name: "Name",
      experience_mapping_field_type: "Type",
      experience_mapping_field_order: "Order",
      experience_mapping_field_duration: "Duration",
      experience_mapping_field_properties: "Properties",
      experience_mapping_field_subproperties: "Subproperties",
      experience_mapping_save_changes: "Save changes",
      experience_mapping_saved: "Saved",
      experience_mapping_save_error: "Could not save changes.",
      experience_mapping_delete_story: "Delete star system",
      experience_mapping_delete_scene: "Delete star",
      experience_mapping_delete_action: "Delete action",
      experience_mapping_delete_confirm: "Click again to confirm",
      experience_mapping_delete_error: "Delete failed.",
      experience_mapping_expand_all: "Expand all",
      experience_mapping_collapse_all: "Collapse all",
      experience_mapping_forbidden: "You do not have admin access for this section.",
      experience_mapping_bad_json: "JSON must be valid before saving.",
      experience_mapping_intro: "Edit realm vocabulary from largest to smallest. Generate JSON and paste it in chat for code application.",
      experience_mapping_dirty: "dirty changes",
      experience_mapping_export_ready: "No unsaved mapping edits yet.",
      experience_mapping_export_json: "Generate JSON export",
      experience_mapping_copy_json: "Copy JSON",
      experience_mapping_copied: "Copied",
      experience_mapping_paste_instruction: "Paste this JSON in chat and I will apply it to chuzi-shared.",
      experience_mapping_export_empty: "Export output appears here.",
      experience_mapping_select_realm: "Select realm",
      experience_mapping_group_major_idea: "Major Idea",
      experience_mapping_helper_realm_idea: "Highest-level concept for this realm.",
      experience_mapping_save_experience: "Save to experience",
      experience_mapping_saved_experience: "Saved to current experience. Some labels appear immediately.",
      auth_email_placeholder: "Email",
      auth_password_input_placeholder: "Password",
      auth_password_sign_in_button: "Sign In",
      auth_password_signing_in: "Signing in...",
      auth_signin_tab: "Sign in",
      auth_signup_tab: "Sign up",
      auth_signup_subtitle: "Create your account with secure sign-in.",
      auth_signin_subtitle: "Sign in with your username or email.",
      auth_sign_up_button: "Create account",
      auth_username_placeholder: "Username",
      auth_identifier_placeholder: "Username or email",
      auth_sign_in_button: "Log in",
      auth_continue_with_google: "Continue with Google",
      auth_magic_link_code_placeholder: "Sign-in code",
      auth_magic_link_enter_code: "Enter the code from your email to sign in.",
      auth_magic_link_sent_code: "Check your email for your sign-in code.",
      auth_code_required: "Sign-in code is required.",
      auth_identifier_required: "Username or email is required.",
      auth_identifier_not_found: "No account found with that username.",
      auth_signing_in: "Signing in...",
      auth_oidc_redirecting: "Redirecting to secure sign-in...",
      auth_oidc_failed: "Could not start sign-in.",
      auth_oidc_callback_processing: "Completing sign-in...",
      auth_oidc_verified: "Signed in. Redirecting...",
      auth_oidc_callback_failed: "Could not complete sign-in.",
      auth_oidc_token_missing: "Sign-in token was missing.",
      auth_magic_link_sent: "If that email is registered, a magic link has been sent.",
      auth_magic_link_sent_signup: "Check your email for your sign-up link.",
      auth_magic_link_verifying: "Verifying magic link...",
      auth_magic_link_verified: "Signed in. Redirecting...",
      auth_magic_link_failed: "This magic link is invalid or expired.",
      auth_magic_link_cta: "Use another email",
      auth_login_failed: "Sign-in failed.",
      auth_username_checking: "Checking username availability...",
      auth_username_available: "Username is available.",
      auth_username_unavailable: "Username is taken; updated to an available suggestion.",
      auth_username_required: "Username is required.",
      auth_username_update_failed: "Could not reserve a unique username.",
      experience_mapping_group_experience_root: "Experience Root",
      experience_mapping_group_node_types: "Node Types",
      experience_mapping_group_scene_structure: "Scene Structure",
      experience_mapping_group_media: "Media Terms",
      experience_mapping_group_counts: "Counts and Adjacent Labels",
      experience_mapping_helper_story: "Largest narrative unit.",
      experience_mapping_helper_scene: "Contained unit under story.",
      experience_mapping_helper_scene_choice: "Branching choice in a scene.",
      experience_mapping_helper_node: "Smallest flow node.",
      experience_mapping_helper_alt_media: "Smallest related media label."
    },
    locales: {
      es: {
        genre: "Galaxia",
        story: "Sistema Estelar",
        scene: "Estrella",
        scene_choice: "Elecci\xF3n",
        player: "Viajero",
        library: "Carta Estelar",
        editor: "Control de Misi\xF3n",
        publish: "Lanzar",
        the_end: "\xD3rbita Final",
        untitled_story: "Sistema Estelar sin t\xEDtulo",
        menu_create_story: "Nuevo sistema estelar",
        film_title_placeholder: "T\xEDtulo del sistema estelar",
        select_genre: "Seleccionar galaxia",
        created_by: "Dirigido por"
      },
      fr: {
        genre: "Galaxie",
        story: "Syst\xE8me Stellaire",
        scene: "\xC9toile",
        scene_choice: "Choix",
        player: "Voyageur",
        library: "Carte Stellaire",
        editor: "Contr\xF4le de Mission",
        publish: "Lancer",
        the_end: "Orbite Finale",
        untitled_story: "Syst\xE8me stellaire sans titre",
        menu_create_story: "Nouveau syst\xE8me stellaire",
        film_title_placeholder: "Titre du syst\xE8me stellaire",
        select_genre: "S\xE9lectionner une galaxie",
        created_by: "R\xE9alis\xE9 par"
      },
      de: {
        genre: "Galaxie",
        story: "Sternsystem",
        scene: "Stern",
        player: "Reisender",
        publish: "Starten"
      },
      pt: {
        genre: "Gal\xE1xia",
        story: "Sistema Estelar",
        scene: "Estrela",
        player: "Viajante",
        publish: "Lan\xE7ar"
      }
    }
  },
  wilds: {
    label: "CHUZI WILDS",
    short_label: "Wilds",
    lexicon: {
      genre: "Biome",
      story: "Grove",
      scene: "Clearing",
      scene_choice: "Trail",
      node: "Seed",
      node_type_choice: "Trail",
      node_type_media: "Bloom",
      node_type_go_to_scene: "Crossing",
      player: "Wanderer",
      library: "Canopy",
      library_open: "Open Canopy",
      library_close: "Close Canopy",
      editor: "Heartwood",
      publish: "Plant",
      experience_realm_idea: "Untamed Nature",
      the_end: "Roots",
      // deprecated — no longer generated
      media_pool: "Undergrowth",
      scene_graph: "Canopy",
      watch_path: "Your trail",
      untitled_story: "Untitled Grove",
      untitled_scene: "Untitled Branch",
      unknown_genre: "Unknown Biome",
      scenes_count: "clearings",
      choices_count: "trails",
      drafts_published: "Drafts + Planted",
      published_ver: "Planted ver.",
      draft: "Draft",
      no_scenes_yet: "No clearings in this canopy yet.",
      no_path_yet: "No trail to show yet.",
      menu_empty_drafts: "No groves yet. Plant one to begin.",
      menu_empty_published: "No planted groves in the canopy.",
      menu_published_heading: "Planted groves",
      menu_create_story: "New grove",
      menu_create_film: "Create film",
      film_title_placeholder: "Grove title",
      select_genre: "Select biome",
      genre_field_aria: "Biome",
      delete_story_verb: "Delete grove",
      title_scene_default: "Trailhead",
      choices_made_stat: "trails taken",
      content_rating: "Field Guide",
      content_rating_field_aria: "Field guide",
      select_content_rating: "Select field guide",
      unrated: "Unmarked",
      viewer_credits: "Sap",
      creator_credits: "Pollen",
      editor_media_upload: "Upload Bloom",
      editor_media_preview: "Bloom Preview",
      editor_alt_media: "Effects",
      editor_choices: "Choices",
      editor_scene_settings: "Clearing Settings",
      editor_scene_type_label: "Clearing",
      editor_title_scene: "Start",
      editor_film_title: "Grove Title",
      editor_film_title_hint: "Click to rename this grove",
      editor_ending_scene: "End",
      editor_play: "Unfurl",
      editor_pause: "Rest",
      editor_prev_frame: "Previous Frame",
      editor_next_frame: "Next Frame",
      editor_seek: "Seek",
      editor_no_media: "No bloom assigned",
      editor_uploading: "Blooming\u2026",
      editor_drop_media: "Drop bloom here",
      editor_scene_type: "Clearing Type",
      editor_save: "Engrave",
      editor_cancel: "Cancel",
      editor_create_scene: "Plant Clearing",
      editor_create_scene_plain: "Create New Scene",
      editor_create_scene_before: "Insert New Clearing Before",
      editor_create_scene_after: "Insert New Clearing After",
      editor_create_scene_ahead: "Plant Next Clearing",
      editor_create_next_scene: "Grow Next Branch",
      editor_create_scene_cost: "1 Pollen",
      editor_delete_scene_cta: "Delete Scene",
      editor_delete_scene_confirm: "Click again to confirm",
      editor_scene_locked_delete: "Start scene cannot be deleted.",
      editor_scene_color: "Clearing Tint",
      viewer_continue: "Press On",
      chuzi_ai_name: "Chuzi AI",
      chuzi_ai_create_with: "Create with Chuzi AI",
      chuzi_ai_create_manual: "Enter title manually",
      chuzi_ai_intro: "Answer a few story prompts and Chuzi AI will grow your short story.",
      chuzi_ai_step_of: "Step",
      chuzi_ai_back: "Back",
      chuzi_ai_next: "Next",
      chuzi_ai_scenes_label: "scenes",
      chuzi_ai_creating: "Creating\u2026",
      chuzi_ai_generate_failed: "Story generation failed.",
      chuzi_ai_build_failed: "Failed to build story.",
      chuzi_ai_generate: "Generate scene",
      chuzi_ai_generating: "Generating\u2026",
      chuzi_ai_token_in: "Tokens in",
      chuzi_ai_token_out: "Tokens out",
      chuzi_ai_credits_cost: "AI credits",
      chuzi_ai_scene_credits: "Scene credits",
      chuzi_ai_total_cost: "Total create credits",
      chuzi_ai_insufficient: "Not enough create credits for this generation.",
      chuzi_ai_apply: "Build story",
      chuzi_ai_applying: "Building scenes\u2026",
      chuzi_ai_scene_create: "Create scene with Chuzi AI",
      chuzi_ai_scene_overwrite: "Rewrite scene with Chuzi AI",
      chuzi_ai_scene_brief: "Scene direction",
      chuzi_ai_scene_brief_hint: "Describe what should happen in this scene. Chuzi AI reads your existing story for characters, tone, and style.",
      chuzi_ai_scene_context_note: "Uses your story\u2019s existing scenes, typography, and art direction as context.",
      chuzi_ai_scene_overwrite_confirm: "I understand this replaces this scene\u2019s text and styling.",
      chuzi_ai_scene_apply_create: "Create scene",
      chuzi_ai_scene_apply_overwrite: "Replace scene",
      chuzi_ai_scene_continue_default: "Continue the story with the next scene. Leave the narrative open-ended.",
      chuzi_ai_scene_continue_vibes: "Stay in the same tone and genre ({vibes}).",
      chuzi_ai_scene_continue_characters: "Keep roughly {count} characters in play.",
      chuzi_ai_scene_continue_hook: "Write the next scene continuing from where the story left off. Do not end or resolve the story.",
      editor_scene_nav: "Scene navigation",
      editor_scene_title_hint: "Click to rename this clearing",
      chuzi_ai_step_protagonist_hint: "Who is your main character? Name, age, personality, and what makes them interesting.",
      chuzi_ai_step_setting: "Setting",
      chuzi_ai_step_setting_hint: "Where and when does the story begin? Describe the world and their everyday life.",
      chuzi_ai_step_conflict: "Conflict",
      chuzi_ai_step_conflict_hint: "What event or problem kicks the story into motion?",
      chuzi_ai_step_journey: "Journey",
      chuzi_ai_step_journey_hint: "What challenges, turning points, climax, and resolution shape the story?",
      chuzi_ai_step_tone: "Tone & mood",
      chuzi_ai_step_tone_hint: "Genre, mood, and atmosphere (e.g. bittersweet sci-fi, cozy mystery, epic fantasy).",
      chuzi_ai_monomyth_hero: "Hero\u2019s Journey: the protagonist",
      chuzi_ai_monomyth_world: "Monomyth: ordinary world",
      chuzi_ai_monomyth_you: "Story Circle: zone of comfort",
      chuzi_ai_monomyth_need: "Story Circle: something isn\u2019t right",
      chuzi_ai_monomyth_go: "Monomyth: crossing the first threshold",
      chuzi_ai_monomyth_search: "Monomyth: road of trials",
      chuzi_ai_monomyth_find: "Monomyth: the ultimate boon",
      chuzi_ai_monomyth_take: "Monomyth: atonement with the father",
      chuzi_ai_monomyth_return: "Monomyth: crossing the return threshold",
      chuzi_ai_monomyth_change: "Monomyth: freedom to live",
      cosmos_cluster_published: "Ancient Grove",
      cosmos_cluster_unpublished: "Seedling Nursery",
      cosmos_cluster_limited: "Canopy Clearing",
      cosmos_cluster_jettisoned: "Deadfall",
      jettison_unjettison: "Regrow",
      jettison_promote: "Return to Heartwood",
      jettison_purge_story: "Burn",
      jettison_purge_all: "Burn All",
      jettison_days_remaining: "{days}d remaining",
      nav_bar_empty_nebula: "No groves",
      nebula_chuzi: "CHUZI!",
      published_version_label: "Version",
      system_story_create: "New reserved story",
      publish_tier_private: "Private",
      publish_tier_limited: "Limited access",
      publish_tier_worldwide: "Worldwide",
      hud_unpublish_control: "Uproot",
      hud_retract_control: "Retract",
      unpublish_confirm_hint: "Tap again to confirm",
      create_publish_hint: "Add ratings, genre & coverbox when you bloom.",
      generator_create_upload: "Create / Upload Bloom",
      generator_generate: "Generate with Chuzi AI",
      generator_vibes_prompt: "Choose your vibes",
      generator_vibes_required: "Pick at least one vibe.",
      generator_audience_prompt: "Who\u2019s your audience?",
      generator_audience_placeholder: "Select reading level",
      generator_characters_prompt: "How many characters?",
      generator_details_prompt: "Any details to add?",
      generator_details_placeholder: "Describe your world\u2026",
      generator_record_cta: "Whisper your world",
      editor_ai_generate: "Grow Bloom",
      editor_ai_generating: "Seed Sprouting\u2026",
      editor_ai_mood_question: "What feeling should this clearing evoke?",
      editor_ai_style_question: "What growth pattern for this bloom?",
      editor_ai_prompt_question: "Describe this clearing",
      editor_ai_accept: "Root Bloom",
      editor_ai_reject: "Prune Bloom",
      editor_ai_retry: "Regrow",
      editor_ai_feedback_prompt: "How should we reshape the growth?",
      editor_ai_credit_cost: "1 Pollen per bloom",
      spending_modal_title: "Gather More Pollen",
      spending_modal_body: "You need more credits to continue. Choose how many to add.",
      spending_modal_no_payment: "Add a payment method to purchase credits.",
      spending_modal_credits_label: "Credits to add",
      spending_modal_price_label: "Total",
      spending_modal_admin_note: "Admin \u2014 credits are granted free of charge.",
      spending_modal_purchase: "Purchase Credits",
      spending_modal_grant: "Grant Credits",
      spending_modal_add_card: "Add Payment Method",
      spending_modal_card_last_four: "Card last four digits",
      spending_modal_card_label: "Card label (optional)",
      spending_modal_processing: "Processing\u2026",
      spending_modal_success: "Credits added.",
      spending_modal_error: "Could not complete purchase. Try again.",
      spending_modal_cancel: "Not now",
      spending_insufficient_credits: "Not enough credits. Purchase more to continue.",
      spending_add_credits_continue: "Add credits to continue",
      editor_ai_generating_scene: "Clearing emerging\u2026",
      editor_ai_style_badge: "Growth Style",
      editor_ai_failed: "Growth withered. Try regrowing.",
      editor_media_attached: "This clearing already has a bloom rooted.",
      editor_remove_media: "Uproot Bloom",
      editor_media_question: "What's this scene's media?",
      editor_media_type_text: "Text",
      editor_media_type_imagery: "Imagery",
      editor_media_type_film: "Film",
      editor_media_preview_cta: "Preview",
      editor_media_preview_disabled: "Add text to enable preview.",
      editor_text_toolbar_label: "Text / Video",
      editor_text_font_label: "Font",
      editor_text_color_label: "Text Color",
      editor_text_background_label: "Background",
      editor_text_font_size_label: "Font Size",
      editor_text_placeholder: "Start writing scene media...",
      editor_line_add: "Add Text Block",
      editor_add_text: "Add Text",
      editor_add_video: "Add Video",
      editor_text_lines_section: "Transmission Lines",
      editor_line_editor_label: "Editor",
      editor_line_number: "Line",
      editor_select_line_hint: "Select a transmission line to edit",
      editor_select_effect_hint: "Select an effect to edit",
      editor_select_choice_hint: "Select a choice to edit",
      editor_line_timestamp: "Appears at",
      editor_line_fade_in: "Fade In",
      editor_line_fade_out: "Fade Out",
      editor_line_duration: "Duration",
      editor_line_persist: "Stay on Screen",
      editor_effects_section: "Effects",
      editor_effects_add: "Add sounds, effects, etc",
      editor_add_sound_music: "Add Sound / Music",
      editor_add_fx: "Add FX",
      editor_node_list_section: "Bloom Layers",
      editor_exit_choice_label: "Go To The End",
      editor_bg_layer_label: "Background",
      editor_remove_layer: "Remove",
      editor_new_node: "New\u2026",
      editor_new_text: "Lines of Text",
      editor_new_choice: "Trail",
      editor_new_image: "Image",
      editor_new_video: "Video",
      editor_new_music: "Music",
      editor_new_sound: "Sound Effect",
      editor_new_logic: "Logic",
      editor_new_credits: "Chosen by\u2026",
      editor_credits_layer: "Credits",
      editor_logic_mode_set: "Set Variable",
      editor_logic_mode_evaluate: "Evaluate",
      editor_logic_variable_label: "Variable",
      editor_logic_value_label: "Value",
      editor_logic_operator_label: "Condition",
      editor_logic_then_layers: "Then activate layers",
      editor_logic_new_variable: "New variable\u2026",
      editor_insert_star_menu: "Insert New Clearing\u2026",
      editor_select_node_hint: "Select a layer to edit",
      editor_effect_describe_prompt: "Describe what you want in detail",
      editor_effect_share_toggle: "Allow others to use this?",
      editor_effect_share_yes: "Yes, share with others",
      editor_effect_share_no: "No, keep it private",
      editor_effect_generating: "Generating\u2026",
      editor_effect_fade_in_sound: "Fade In Sound",
      editor_effect_fade_out_sound: "Fade Out Sound",
      editor_effect_fade_in_visual: "Fade In",
      editor_effect_fade_out_visual: "Fade Out",
      editor_timeline_label: "Timeline",
      editor_preview_label: "Preview",
      editor_preview_empty_hint: "Await the bloom",
      editor_preview_interact_hint: "Preview \u2014 drag bloom to position, resize from corner",
      editor_preview_play: "Play",
      editor_preview_pause: "Pause",
      editor_media_placeholder_imagery: "Imagery authoring is coming soon.",
      editor_media_placeholder_film: "Film authoring is coming soon.",
      editor_goto_scene: "Goto Clearing",
      editor_scene_duration: "Bloom Duration",
      editor_add_choice: "Add Trail",
      editor_arrow_tool: "Place Indicator",
      editor_goto_nullified: "Disabled \u2014 trails override goto",
      editor_remove_choice: "Remove Trail",
      editor_choice_target: "Destination Clearing",
      editor_choices_empty_hint: "Set a goto clearing or add a trail to branch the story.",
      editor_goto_none: "None",
      editor_choice_label: "Trail",
      editor_choice_display_label: "Trail Label",
      editor_choice_display_label_hint: "Leave blank to hide the label beneath the indicator. Lexicon keys like viewer_continue resolve to realm text.",
      editor_choice_display_label_empty: "No label",
      editor_choice_display_label_preview: "Preview",
      editor_choice_exit_target_hint: "Terminal exit \u2014 ends the story for viewers.",
      editor_flow_timeline: "Story Flow",
      editor_flow_goto_path: "Default Goto",
      editor_flow_add_choice_here: "Add Trail Here",
      editor_flow_choice_branch: "Branch",
      editor_flow_content_ref: "Content",
      editor_scene_duration_unit: "seconds",
      editor_scene_duration_override_hint: "Overrides bloom length. Content runtime: {duration}s",
      editor_arrow_scale: "Span",
      editor_arrow_rotation: "Bearing",
      editor_arrow_rotate_hint: "Drag the beacon above the indicator in preview to rotate",
      constellation_no_coverbox: "Unseen",
      hud_edit_control: "Tend",
      hud_delete_control: "Uproot",
      hud_publish_control: "Plant",
      hud_launch_control: "Enter",
      editor_publish_story: "Share",
      viewer_loading: "Entering the grove\u2026",
      viewer_preparing: "Gathering blooms\u2026",
      viewer_close: "Leave",
      viewer_complete: "End Journey",
      viewer_stage_label: "Canopy",
      viewer_choose_path: "Choose",
      viewer_scrub_label: "Playback position",
      create_button: "CREATE",
      publish_form_heading: "Planting check",
      tags_label: "Root Tags",
      tags_placeholder: "Search root tags\u2026",
      tags_empty: "No root tags yet",
      tags_required: "At least one root tag is required.",
      tags_clear: "Clear tag filter",
      all_nav_search_placeholder: "Seek trails\u2026",
      all_nav_sort_label: "Order by",
      all_nav_empty_nebula: "No groves",
      catalog_order_label: "Sort by",
      catalog_order_newest: "Newest Sprout",
      catalog_order_oldest: "Oldest Sprout",
      catalog_order_most_viewed: "Most viewed",
      catalog_order_least_viewed: "Least viewed",
      error_rate_limited: "The grove needs a breath. Wait a moment and try again.",
      story_picker_label: "Groves",
      created_by: "Tended by",
      chosen_by: "Chosen by",
      story_metadata_heading: "Grove details",
      story_metadata_save: "Save",
      constellation_title_edit_hint: "Edit grove title and details",
      director_profile_title: "Director profile",
      director_profile_name_label: "Name",
      director_profile_realm_label: "Experience realm",
      director_profile_image_label: "Image",
      director_profile_image_hint: "Realm-themed seal \u2014 switching the realm above swaps the seal. Custom uploads land with the V1 avatar endpoints.",
      director_profile_tab_profile: "Profile",
      director_profile_tab_experience_mapping: "Experience Mapping",
      director_profile_admin_only: "Admin only",
      director_profile_cancel: "Cancel",
      director_profile_close: "Close",
      director_profile_save: "Save",
      director_profile_saving: "Saving\u2026",
      director_profile_name_required: "Name is required.",
      director_profile_username_locked_hint: "Trail name set at first bloom \u2014 cannot be changed.",
      director_profile_save_failed: "Save failed",
      experience_mapping_loading: "Loading experience mapping\u2026",
      experience_mapping_empty: "No template hierarchy available yet.",
      experience_mapping_retry: "Retry",
      experience_mapping_story_label: "Story",
      experience_mapping_scene_label: "Scene",
      experience_mapping_action_label: "Action",
      experience_mapping_field_title: "Title",
      experience_mapping_field_description: "Description",
      experience_mapping_field_genre: "Genre",
      experience_mapping_field_content_rating: "Content rating",
      experience_mapping_field_color: "Color",
      experience_mapping_field_name: "Name",
      experience_mapping_field_type: "Type",
      experience_mapping_field_order: "Order",
      experience_mapping_field_duration: "Duration",
      experience_mapping_field_properties: "Properties",
      experience_mapping_field_subproperties: "Subproperties",
      experience_mapping_save_changes: "Save changes",
      experience_mapping_saved: "Saved",
      experience_mapping_save_error: "Could not save changes.",
      experience_mapping_delete_story: "Delete story",
      experience_mapping_delete_scene: "Delete scene",
      experience_mapping_delete_action: "Delete action",
      experience_mapping_delete_confirm: "Click again to confirm",
      experience_mapping_delete_error: "Delete failed.",
      experience_mapping_expand_all: "Expand all",
      experience_mapping_collapse_all: "Collapse all",
      experience_mapping_forbidden: "You do not have admin access for this section.",
      experience_mapping_bad_json: "JSON must be valid before saving.",
      experience_mapping_intro: "Edit realm vocabulary from largest to smallest. Generate JSON and paste it in chat for code application.",
      experience_mapping_dirty: "dirty changes",
      experience_mapping_export_ready: "No unsaved mapping edits yet.",
      experience_mapping_export_json: "Generate JSON export",
      experience_mapping_copy_json: "Copy JSON",
      experience_mapping_copied: "Copied",
      experience_mapping_paste_instruction: "Paste this JSON in chat and I will apply it to chuzi-shared.",
      experience_mapping_export_empty: "Export output appears here.",
      experience_mapping_select_realm: "Select realm",
      experience_mapping_group_major_idea: "Major Idea",
      experience_mapping_helper_realm_idea: "Highest-level concept for this realm.",
      experience_mapping_save_experience: "Save to experience",
      experience_mapping_saved_experience: "Saved to current experience. Some labels appear immediately.",
      auth_email_placeholder: "Email",
      auth_password_input_placeholder: "Password",
      auth_password_sign_in_button: "Sign In",
      auth_password_signing_in: "Signing in...",
      auth_signin_tab: "Sign in",
      auth_signup_tab: "Sign up",
      auth_signup_subtitle: "Create your account with secure sign-in.",
      auth_signin_subtitle: "Sign in with your username or email.",
      auth_sign_up_button: "Create account",
      auth_username_placeholder: "Username",
      auth_identifier_placeholder: "Username or email",
      auth_sign_in_button: "Log in",
      auth_continue_with_google: "Continue with Google",
      auth_magic_link_code_placeholder: "Sign-in code",
      auth_magic_link_enter_code: "Enter the code from your email to sign in.",
      auth_magic_link_sent_code: "Check your email for your sign-in code.",
      auth_code_required: "Sign-in code is required.",
      auth_identifier_required: "Username or email is required.",
      auth_identifier_not_found: "No account found with that username.",
      auth_signing_in: "Signing in...",
      auth_oidc_redirecting: "Redirecting to secure sign-in...",
      auth_oidc_failed: "Could not start sign-in.",
      auth_oidc_callback_processing: "Completing sign-in...",
      auth_oidc_verified: "Signed in. Redirecting...",
      auth_oidc_callback_failed: "Could not complete sign-in.",
      auth_oidc_token_missing: "Sign-in token was missing.",
      auth_magic_link_sent: "If that email is registered, a magic link has been sent.",
      auth_magic_link_sent_signup: "Check your email for your sign-up link.",
      auth_magic_link_verifying: "Verifying magic link...",
      auth_magic_link_verified: "Signed in. Redirecting...",
      auth_magic_link_failed: "This magic link is invalid or expired.",
      auth_magic_link_cta: "Use another email",
      auth_login_failed: "Sign-in failed.",
      auth_username_checking: "Checking username availability...",
      auth_username_available: "Username is available.",
      auth_username_unavailable: "Username is taken; updated to an available suggestion.",
      auth_username_required: "Username is required.",
      auth_username_update_failed: "Could not reserve a unique username.",
      experience_mapping_group_experience_root: "Experience Root",
      experience_mapping_group_node_types: "Node Types",
      experience_mapping_group_scene_structure: "Scene Structure",
      experience_mapping_group_media: "Media Terms",
      experience_mapping_group_counts: "Counts and Adjacent Labels",
      experience_mapping_helper_story: "Largest narrative unit.",
      experience_mapping_helper_scene: "Contained unit under story.",
      experience_mapping_helper_scene_choice: "Branching choice in a scene.",
      experience_mapping_helper_node: "Smallest flow node.",
      experience_mapping_helper_alt_media: "Smallest related media label."
    },
    locales: {
      es: {
        genre: "Bioma",
        story: "Arboleda",
        scene: "Claro",
        scene_choice: "Sendero",
        player: "Errante",
        library: "Dosel",
        editor: "Duramen",
        publish: "Plantar",
        the_end: "Ra\xEDces",
        untitled_story: "Arboleda sin t\xEDtulo",
        menu_create_story: "Nueva arboleda",
        film_title_placeholder: "T\xEDtulo de la arboleda",
        select_genre: "Seleccionar bioma",
        created_by: "Cuidado por"
      },
      fr: {
        genre: "Biome",
        story: "Bosquet",
        scene: "Clairi\xE8re",
        scene_choice: "Sentier",
        player: "Vagabond",
        library: "Canop\xE9e",
        editor: "C\u0153ur de bois",
        publish: "Planter",
        the_end: "Racines",
        untitled_story: "Bosquet sans titre",
        menu_create_story: "Nouveau bosquet",
        film_title_placeholder: "Titre du bosquet",
        select_genre: "S\xE9lectionner un biome",
        created_by: "Soign\xE9 par"
      },
      de: {
        genre: "Biom",
        story: "Hain",
        scene: "Lichtung",
        player: "Wanderer",
        publish: "Pflanzen"
      },
      pt: {
        genre: "Bioma",
        story: "Bosque",
        scene: "Clareira",
        player: "Andarilho",
        publish: "Plantar"
      }
    }
  }
};
var FALLBACK_LEXICON = {
  genre: "Genre",
  story: "Story",
  scene: "Scene",
  scene_choice: "Choice",
  node: "Node",
  node_type_choice: "Choice",
  node_type_media: "Media",
  node_type_go_to_scene: "Go to scene",
  player: "Player",
  library: "Library",
  library_open: "Open scene tree",
  library_close: "Close scene tree",
  editor: "Editor",
  publish: "Publish",
  experience_realm_idea: "Experience Theme",
  the_end: "The End",
  // deprecated — no longer generated
  media_pool: "Media Pool",
  scene_graph: "Scene tree",
  watch_path: "Your path",
  untitled_story: "Untitled Story",
  untitled_scene: "Untitled Scene",
  unknown_genre: "Unknown Genre",
  scenes_count: "scenes",
  choices_count: "choices",
  drafts_published: "Drafts + Published",
  published_ver: "Published ver.",
  draft: "Draft",
  no_scenes_yet: "No scenes available yet.",
  no_path_yet: "No path to show yet.",
  menu_empty_drafts: "No films yet. Create one to get started.",
  menu_empty_published: "No published films found.",
  menu_published_heading: "Published Films",
  menu_create_story: "Create Story",
  menu_create_film: "Create Film",
  film_title_placeholder: "Film title",
  select_genre: "Select genre",
  genre_field_aria: "Film genre",
  delete_story_verb: "Delete film",
  title_scene_default: "Start",
  choices_made_stat: "choices made",
  content_rating: "Rating",
  content_rating_field_aria: "Content rating",
  select_content_rating: "Select rating",
  unrated: "Not Rated",
  viewer_credits: "Viewer Credits",
  creator_credits: "Creator Credits",
  editor_media_upload: "Upload Media",
  editor_media_preview: "Media Preview",
  editor_alt_media: "Effects",
  editor_choices: "Choices",
  editor_scene_settings: "Scene Settings",
  editor_scene_type_label: "Scene",
  editor_title_scene: "Start",
  editor_film_title: "Film Title",
  editor_film_title_hint: "Click to rename this film",
  editor_ending_scene: "End",
  editor_play: "Play",
  editor_pause: "Pause",
  editor_prev_frame: "Previous Frame",
  editor_next_frame: "Next Frame",
  editor_seek: "Seek",
  editor_no_media: "No media assigned",
  editor_uploading: "Uploading\u2026",
  editor_drop_media: "Drop media here",
  editor_scene_type: "Scene Type",
  editor_save: "Save",
  editor_cancel: "Cancel",
  editor_create_scene: "Create Scene",
  editor_create_scene_plain: "Create New Scene",
  editor_create_scene_before: "Insert new scene before",
  editor_create_scene_after: "Insert new scene after",
  editor_create_scene_ahead: "Create new scene",
  editor_create_next_scene: "Create next scene",
  editor_create_scene_cost: "1 credit",
  editor_delete_scene_cta: "Delete Scene",
  editor_delete_scene_confirm: "Click again to confirm",
  editor_scene_locked_delete: "Start scene cannot be deleted.",
  editor_scene_color: "Scene Color",
  chuzi_ai_name: "Chuzi AI",
  chuzi_ai_create_with: "Create with Chuzi AI",
  chuzi_ai_create_manual: "Enter title manually",
  chuzi_ai_intro: "Answer a few story prompts and Chuzi AI will draft your short story.",
  chuzi_ai_step_of: "Step",
  chuzi_ai_back: "Back",
  chuzi_ai_next: "Next",
  chuzi_ai_scenes_label: "scenes",
  chuzi_ai_creating: "Creating\u2026",
  chuzi_ai_generate_failed: "Story generation failed.",
  chuzi_ai_build_failed: "Failed to build story.",
  chuzi_ai_generate: "Generate scene",
  chuzi_ai_generating: "Generating\u2026",
  chuzi_ai_token_in: "Tokens in",
  chuzi_ai_token_out: "Tokens out",
  chuzi_ai_credits_cost: "AI credits",
  chuzi_ai_scene_credits: "Scene credits",
  chuzi_ai_total_cost: "Total create credits",
  chuzi_ai_insufficient: "Not enough create credits for this generation.",
  chuzi_ai_apply: "Build story",
  chuzi_ai_applying: "Building scenes\u2026",
  wizard_back: "Back",
  wizard_next: "Next",
  wizard_vibes_hint: "Pick the genres and moods for your scene. Select multiple.",
  wizard_audience_hint: "Controls vocabulary, sentence length, and themes.",
  wizard_details_placeholder: "What happens next?",
  chuzi_ai_scene_create: "Create scene with Chuzi AI",
  chuzi_ai_scene_overwrite: "Rewrite scene with Chuzi AI",
  chuzi_ai_scene_brief: "Scene direction",
  chuzi_ai_scene_brief_hint: "Describe what should happen in this scene. Chuzi AI reads your existing story for characters, tone, and style.",
  chuzi_ai_scene_context_note: "Uses your story\u2019s existing scenes, typography, and art direction as context.",
  chuzi_ai_scene_overwrite_confirm: "I understand this replaces this scene\u2019s text and styling.",
  chuzi_ai_scene_apply_create: "Create scene",
  chuzi_ai_scene_apply_overwrite: "Replace scene",
  chuzi_ai_scene_continue_default: "Continue the story with the next scene. Leave the narrative open-ended.",
  chuzi_ai_scene_continue_vibes: "Stay in the same tone and genre ({vibes}).",
  chuzi_ai_scene_continue_characters: "Keep roughly {count} characters in play.",
  chuzi_ai_scene_continue_hook: "Write the next scene continuing from where the story left off. Do not end or resolve the story.",
  editor_scene_nav: "Scene navigation",
  editor_scene_title_hint: "Click to rename this scene",
  chuzi_ai_step_protagonist: "Protagonist",
  chuzi_ai_step_protagonist_hint: "Who is your main character? Name, age, personality, and what makes them interesting.",
  chuzi_ai_step_setting: "Setting",
  chuzi_ai_step_setting_hint: "Where and when does the story begin? Describe the world and their everyday life.",
  chuzi_ai_step_conflict: "Conflict",
  chuzi_ai_step_conflict_hint: "What event or problem kicks the story into motion?",
  chuzi_ai_step_journey: "Journey",
  chuzi_ai_step_journey_hint: "What challenges, turning points, climax, and resolution shape the story?",
  chuzi_ai_step_tone: "Tone & mood",
  chuzi_ai_step_tone_hint: "Genre, mood, and atmosphere (e.g. bittersweet sci-fi, cozy mystery, epic fantasy).",
  viewer_continue: "Continue",
  cosmos_cluster_published: "Published",
  cosmos_cluster_unpublished: "Unpublished",
  cosmos_cluster_limited: "Limited",
  cosmos_cluster_jettisoned: "Jettisoned",
  jettison_unjettison: "Recover",
  jettison_promote: "Promote",
  jettison_purge_story: "Delete Permanently",
  jettison_purge_all: "Purge All",
  jettison_days_remaining: "{days}d remaining",
  nav_bar_empty_nebula: "No stories",
  nebula_chuzi: "CHUZI!",
  published_version_label: "Version",
  system_story_create: "New reserved story",
  publish_tier_private: "Private",
  publish_tier_limited: "Limited access",
  publish_tier_worldwide: "Worldwide",
  hud_unpublish_control: "Unpublish",
  hud_retract_control: "Retract",
  unpublish_confirm_hint: "Tap again to confirm",
  create_publish_hint: "You can add ratings, genre, and cover art when you publish.",
  generator_create_upload: "Create / Upload",
  generator_generate: "Generate",
  generator_vibes_prompt: "Vibes (choose as many as you\u2019d like)",
  generator_vibes_required: "Pick at least one vibe.",
  generator_audience_prompt: "Who\u2019s your audience?",
  generator_audience_placeholder: "Select reading level",
  generator_characters_prompt: "How many characters?",
  generator_details_prompt: "Any details to add?",
  generator_details_placeholder: "Add any details to help guide the story\u2026",
  generator_record_cta: "Speak your vision",
  chuzi_ai_monomyth_hero: "Hero\u2019s Journey: the protagonist",
  chuzi_ai_monomyth_world: "Monomyth: ordinary world",
  chuzi_ai_monomyth_you: "Story Circle: zone of comfort",
  chuzi_ai_monomyth_need: "Story Circle: something isn\u2019t right",
  chuzi_ai_monomyth_go: "Monomyth: crossing the first threshold",
  chuzi_ai_monomyth_search: "Monomyth: road of trials",
  chuzi_ai_monomyth_find: "Monomyth: the ultimate boon",
  chuzi_ai_monomyth_take: "Monomyth: atonement with the father",
  chuzi_ai_monomyth_return: "Monomyth: crossing the return threshold",
  chuzi_ai_monomyth_change: "Monomyth: freedom to live",
  editor_ai_generate: "Generate with AI",
  editor_ai_generating: "Generating\u2026",
  editor_ai_mood_question: "What mood should this scene convey?",
  editor_ai_style_question: "What visual style do you want?",
  editor_ai_prompt_question: "Describe this scene",
  editor_ai_accept: "Accept",
  editor_ai_reject: "Reject",
  editor_ai_retry: "Try Again",
  editor_ai_feedback_prompt: "What should we change?",
  editor_ai_credit_cost: "1 credit per generation",
  spending_modal_title: "Purchase Credits",
  spending_modal_body: "You need more credits to continue. Choose how many to add.",
  spending_modal_no_payment: "Add a payment method to purchase credits.",
  spending_modal_credits_label: "Credits to add",
  spending_modal_price_label: "Total",
  spending_modal_admin_note: "Admin \u2014 credits are granted free of charge.",
  spending_modal_purchase: "Purchase Credits",
  spending_modal_grant: "Grant Credits",
  spending_modal_add_card: "Add Payment Method",
  spending_modal_card_last_four: "Card last four digits",
  spending_modal_card_label: "Card label (optional)",
  spending_modal_processing: "Processing\u2026",
  spending_modal_success: "Credits added.",
  spending_modal_error: "Could not complete purchase. Try again.",
  spending_modal_cancel: "Not now",
  spending_insufficient_credits: "Not enough credits. Purchase more to continue.",
  spending_add_credits_continue: "Add credits to continue",
  editor_ai_generating_scene: "Scene generating\u2026",
  editor_ai_style_badge: "Style",
  editor_ai_failed: "Generation failed. Please try again.",
  editor_media_attached: "This scene already has media attached.",
  editor_remove_media: "Remove Media",
  editor_media_question: "What's this scene's media?",
  editor_media_type_text: "Text",
  editor_media_type_imagery: "Imagery",
  editor_media_type_film: "Film",
  editor_media_preview_cta: "Preview",
  editor_media_preview_disabled: "Add text to enable preview.",
  editor_text_toolbar_label: "Text / Video",
  editor_text_font_label: "Font",
  editor_text_color_label: "Text Color",
  editor_text_background_label: "Background",
  editor_text_font_size_label: "Font Size",
  editor_text_placeholder: "Start writing scene media...",
  editor_line_add: "Add Text Block",
  editor_add_text: "Add Text",
  editor_add_video: "Add Video",
  editor_text_lines_section: "Text Lines",
  editor_line_editor_label: "Editor",
  editor_line_number: "Line",
  editor_select_line_hint: "Select a line to edit",
  editor_select_effect_hint: "Select an effect to edit",
  editor_select_choice_hint: "Select a choice to edit",
  editor_line_timestamp: "Appears at",
  editor_line_fade_in: "Fade In",
  editor_line_fade_out: "Fade Out",
  editor_line_duration: "Duration",
  editor_line_persist: "Stay on Screen",
  editor_effects_section: "Effects",
  editor_effects_add: "Add sounds, effects, etc",
  editor_add_sound_music: "Add Sound / Music",
  editor_add_fx: "Add FX",
  editor_node_list_section: "Layers",
  editor_exit_choice_label: "Go To The End",
  editor_bg_layer_label: "Background",
  editor_remove_layer: "Remove",
  editor_new_node: "New\u2026",
  editor_new_text: "Lines of Text",
  editor_new_choice: "Choice",
  editor_new_image: "Image",
  editor_new_video: "Video",
  editor_new_music: "Music",
  editor_new_sound: "Sound Effect",
  editor_new_logic: "Logic",
  editor_new_credits: "Chosen by\u2026",
  editor_credits_layer: "Credits",
  editor_logic_mode_set: "Set Variable",
  editor_logic_mode_evaluate: "Evaluate",
  editor_logic_variable_label: "Variable",
  editor_logic_value_label: "Value",
  editor_logic_operator_label: "Condition",
  editor_logic_then_layers: "Then activate layers",
  editor_logic_new_variable: "New variable\u2026",
  editor_insert_star_menu: "Insert New Scene\u2026",
  editor_select_node_hint: "Select a layer to edit",
  editor_effect_describe_prompt: "Describe what you want in detail",
  editor_effect_share_toggle: "Allow others to use this?",
  editor_effect_share_yes: "Yes, share with others",
  editor_effect_share_no: "No, keep it private",
  editor_effect_generating: "Generating\u2026",
  editor_effect_fade_in_sound: "Fade In Sound",
  editor_effect_fade_out_sound: "Fade Out Sound",
  editor_effect_fade_in_visual: "Fade In",
  editor_effect_fade_out_visual: "Fade Out",
  editor_timeline_label: "Timeline",
  editor_preview_label: "Preview",
  editor_preview_empty_hint: "Preview will appear here",
  editor_preview_interact_hint: "Preview \u2014 drag to position, resize from corner",
  editor_preview_play: "Play",
  editor_preview_pause: "Pause",
  editor_media_placeholder_imagery: "Imagery authoring is coming soon.",
  editor_media_placeholder_film: "Film authoring is coming soon.",
  editor_goto_scene: "Goto Scene",
  editor_scene_duration: "Scene Duration",
  editor_add_choice: "Add Choice",
  editor_arrow_tool: "Place Arrow",
  editor_goto_nullified: "Disabled \u2014 choices override goto",
  editor_remove_choice: "Remove Choice",
  editor_choice_target: "Destination Scene",
  editor_choices_empty_hint: "Set a goto scene or add a choice to create branching paths.",
  editor_goto_none: "None",
  editor_choice_label: "Choice",
  editor_choice_display_label: "Arrow Label",
  editor_choice_display_label_hint: "Leave blank to hide the label beneath the arrow. Lexicon keys like viewer_continue resolve to realm text.",
  editor_choice_display_label_empty: "No label",
  editor_choice_display_label_preview: "Preview",
  editor_choice_exit_target_hint: "Terminal exit \u2014 ends the story for viewers.",
  editor_flow_timeline: "Story Flow",
  editor_flow_goto_path: "Default Goto",
  editor_flow_add_choice_here: "Add Choice Here",
  editor_flow_choice_branch: "Branch",
  editor_flow_content_ref: "Content",
  editor_scene_duration_unit: "seconds",
  editor_scene_duration_override_hint: "Overrides preview playback length. Content: {duration}s",
  editor_arrow_scale: "Scale",
  editor_arrow_rotation: "Rotation",
  editor_arrow_rotate_hint: "Drag the handle above the arrow in preview to rotate",
  constellation_no_coverbox: "No cover",
  hud_edit_control: "Edit",
  hud_delete_control: "Delete",
  hud_publish_control: "Publish",
  hud_launch_control: "Launch",
  editor_publish_story: "Publish",
  viewer_loading: "Loading experience\u2026",
  viewer_preparing: "Cueing media\u2026",
  viewer_close: "Exit",
  viewer_complete: "End",
  viewer_stage_label: "Experience",
  viewer_choose_path: "Choose",
  viewer_scrub_label: "Playback position",
  create_button: "CREATE",
  publish_form_heading: "Publish checklist",
  tags_label: "Tags",
  tags_placeholder: "Search tags\u2026",
  tags_empty: "No tags yet",
  tags_required: "At least one tag is required.",
  tags_clear: "Clear tag filter",
  all_nav_search_placeholder: "Search tags\u2026",
  all_nav_sort_label: "Order by",
  all_nav_empty_nebula: "No stories",
  catalog_order_label: "Sort by",
  catalog_order_newest: "Newest",
  catalog_order_oldest: "Oldest",
  catalog_order_most_viewed: "Most viewed",
  catalog_order_least_viewed: "Least viewed",
  error_rate_limited: "Too many requests. Wait a moment and try again.",
  story_picker_label: "Stories",
  created_by: "Created by",
  chosen_by: "Chosen by",
  story_metadata_heading: "Story details",
  story_metadata_save: "Save",
  constellation_title_edit_hint: "Edit story title and details",
  director_profile_title: "Director profile",
  director_profile_name_label: "Name",
  director_profile_realm_label: "Experience realm",
  director_profile_image_label: "Image",
  director_profile_image_hint: "Realm-themed seal \u2014 switching the realm above swaps the seal. Custom uploads land with the V1 avatar endpoints.",
  director_profile_tab_profile: "Profile",
  director_profile_tab_experience_mapping: "Experience Mapping",
  director_profile_admin_only: "Admin only",
  director_profile_cancel: "Cancel",
  director_profile_close: "Close",
  director_profile_save: "Save",
  director_profile_saving: "Saving\u2026",
  director_profile_name_required: "Name is required.",
  director_profile_username_locked_hint: "Username is set at sign up and cannot be changed.",
  director_profile_save_failed: "Save failed",
  experience_mapping_loading: "Loading experience mapping\u2026",
  experience_mapping_empty: "No template hierarchy available yet.",
  experience_mapping_retry: "Retry",
  experience_mapping_story_label: "Story",
  experience_mapping_scene_label: "Scene",
  experience_mapping_action_label: "Action",
  experience_mapping_field_title: "Title",
  experience_mapping_field_description: "Description",
  experience_mapping_field_genre: "Genre",
  experience_mapping_field_content_rating: "Content rating",
  experience_mapping_field_color: "Color",
  experience_mapping_field_name: "Name",
  experience_mapping_field_type: "Type",
  experience_mapping_field_order: "Order",
  experience_mapping_field_duration: "Duration",
  experience_mapping_field_properties: "Properties",
  experience_mapping_field_subproperties: "Subproperties",
  experience_mapping_save_changes: "Save changes",
  experience_mapping_saved: "Saved",
  experience_mapping_save_error: "Could not save changes.",
  experience_mapping_delete_story: "Delete story",
  experience_mapping_delete_scene: "Delete scene",
  experience_mapping_delete_action: "Delete action",
  experience_mapping_delete_confirm: "Click again to confirm",
  experience_mapping_delete_error: "Delete failed.",
  experience_mapping_expand_all: "Expand all",
  experience_mapping_collapse_all: "Collapse all",
  experience_mapping_forbidden: "You do not have admin access for this section.",
  experience_mapping_bad_json: "JSON must be valid before saving.",
  experience_mapping_intro: "Edit realm vocabulary from largest to smallest. Generate JSON and paste it in chat for code application.",
  experience_mapping_dirty: "dirty changes",
  experience_mapping_export_ready: "No unsaved mapping edits yet.",
  experience_mapping_export_json: "Generate JSON export",
  experience_mapping_copy_json: "Copy JSON",
  experience_mapping_copied: "Copied",
  experience_mapping_paste_instruction: "Paste this JSON in chat and I will apply it to chuzi-shared.",
  experience_mapping_export_empty: "Export output appears here.",
  experience_mapping_select_realm: "Select realm",
  experience_mapping_group_major_idea: "Major Idea",
  experience_mapping_helper_realm_idea: "Highest-level concept for this realm.",
  experience_mapping_save_experience: "Save to experience",
  experience_mapping_saved_experience: "Saved to current experience. Some labels appear immediately.",
  auth_email_placeholder: "Email",
  auth_password_input_placeholder: "Password",
  auth_password_sign_in_button: "Sign In",
  auth_password_signing_in: "Signing in...",
  auth_signin_tab: "Sign in",
  auth_signup_tab: "Sign up",
  auth_signup_subtitle: "Create your account with secure sign-in.",
  auth_signin_subtitle: "Sign in with your username or email.",
  auth_sign_up_button: "Create account",
  auth_username_placeholder: "Username",
  auth_identifier_placeholder: "Username or email",
  auth_sign_in_button: "Log in",
  auth_continue_with_google: "Continue with Google",
  auth_magic_link_code_placeholder: "Sign-in code",
  auth_magic_link_enter_code: "Enter the code from your email to sign in.",
  auth_magic_link_sent_code: "Check your email for your sign-in code.",
  auth_code_required: "Sign-in code is required.",
  auth_identifier_required: "Username or email is required.",
  auth_identifier_not_found: "No account found with that username.",
  auth_signing_in: "Signing in...",
  auth_oidc_redirecting: "Redirecting to secure sign-in...",
  auth_oidc_failed: "Could not start sign-in.",
  auth_oidc_callback_processing: "Completing sign-in...",
  auth_oidc_verified: "Signed in. Redirecting...",
  auth_oidc_callback_failed: "Could not complete sign-in.",
  auth_oidc_token_missing: "Sign-in token was missing.",
  auth_magic_link_sent: "If that email is registered, a magic link has been sent.",
  auth_magic_link_sent_signup: "Check your email for your sign-up link.",
  auth_magic_link_verifying: "Verifying magic link...",
  auth_magic_link_verified: "Signed in. Redirecting...",
  auth_magic_link_failed: "This magic link is invalid or expired.",
  auth_magic_link_cta: "Use another email",
  auth_login_failed: "Sign-in failed.",
  auth_username_checking: "Checking username availability...",
  auth_username_available: "Username is available.",
  auth_username_unavailable: "Username is taken; updated to an available suggestion.",
  auth_username_required: "Username is required.",
  auth_username_update_failed: "Could not reserve a unique username.",
  experience_mapping_group_experience_root: "Experience Root",
  experience_mapping_group_node_types: "Node Types",
  experience_mapping_group_scene_structure: "Scene Structure",
  experience_mapping_group_media: "Media Terms",
  experience_mapping_group_counts: "Counts and Adjacent Labels",
  experience_mapping_helper_story: "Largest narrative unit.",
  experience_mapping_helper_scene: "Contained unit under story.",
  experience_mapping_helper_scene_choice: "Branching choice in a scene.",
  experience_mapping_helper_node: "Smallest flow node.",
  experience_mapping_helper_alt_media: "Smallest related media label."
};
var FALLBACK_LOCALES = {
  es: {
    genre: "G\xE9nero",
    story: "Historia",
    scene: "Escena",
    scene_choice: "Elecci\xF3n",
    player: "Reproductor",
    library: "Biblioteca",
    editor: "Editor",
    publish: "Publicar",
    the_end: "Fin",
    untitled_story: "Historia sin t\xEDtulo",
    menu_create_story: "Crear historia",
    menu_create_film: "Crear pel\xEDcula",
    film_title_placeholder: "T\xEDtulo de la pel\xEDcula",
    select_genre: "Seleccionar g\xE9nero",
    delete_story_verb: "Eliminar pel\xEDcula",
    created_by: "Creado por"
  },
  fr: {
    genre: "Genre",
    story: "Histoire",
    scene: "Sc\xE8ne",
    scene_choice: "Choix",
    player: "Lecteur",
    library: "Biblioth\xE8que",
    editor: "\xC9diteur",
    publish: "Publier",
    the_end: "Fin",
    untitled_story: "Histoire sans titre",
    menu_create_story: "Cr\xE9er une histoire",
    menu_create_film: "Cr\xE9er un film",
    film_title_placeholder: "Titre du film",
    select_genre: "S\xE9lectionner un genre",
    delete_story_verb: "Supprimer le film",
    created_by: "Cr\xE9\xE9 par"
  },
  de: {
    genre: "Genre",
    story: "Geschichte",
    scene: "Szene",
    publish: "Ver\xF6ffentlichen",
    the_end: "Ende",
    untitled_story: "Unbenannte Geschichte",
    menu_create_story: "Geschichte erstellen",
    film_title_placeholder: "Filmtitel",
    select_genre: "Genre ausw\xE4hlen",
    created_by: "Erstellt von"
  },
  pt: {
    genre: "G\xEAnero",
    story: "Hist\xF3ria",
    scene: "Cena",
    publish: "Publicar",
    the_end: "Fim",
    untitled_story: "Hist\xF3ria sem t\xEDtulo",
    menu_create_story: "Criar hist\xF3ria",
    film_title_placeholder: "T\xEDtulo do filme",
    select_genre: "Selecionar g\xEAnero",
    created_by: "Criado por"
  }
};
function resolveFallback(locale) {
  const overrides = locale && FALLBACK_LOCALES[locale];
  return overrides ? { ...FALLBACK_LEXICON, ...overrides } : { ...FALLBACK_LEXICON };
}
function resolveRealm(realmId, locale) {
  const realm = REALMS[realmId];
  const overrides = locale && realm.locales ? realm.locales[locale] : void 0;
  const runtimeOverrides = runtimeRealmLexiconOverrides[realmId];
  return {
    ...realm.lexicon,
    ...overrides ?? {},
    ...runtimeOverrides ?? {}
  };
}
function t(realmId, key, fallback = "", locale = null) {
  if (realmId && REALMS[realmId]) {
    const realmLex = resolveRealm(realmId, locale);
    if (realmLex[key] !== void 0) return realmLex[key];
  }
  const fb = resolveFallback(locale);
  return fb[key] ?? fallback;
}
function lexiconForRealm(realmId, locale = null) {
  const fb = resolveFallback(locale);
  if (!realmId || !REALMS[realmId]) {
    return fb;
  }
  return { ...fb, ...resolveRealm(realmId, locale) };
}

export { AUDIENCE_AGE_IDS, AUDIENCE_AGE_OPTIONS, CHUZI_AI_GENERATOR_VIBES, CHUZI_AI_PROMPT_STEPS, CHUZI_AI_SCENE_FONT_IDS, CHUZI_AI_VIBE_LABELS, CONTENT_RATINGS, CONTENT_RATING_IDS, CREDITS_PER_DOLLAR, CREDIT_CENTS_PER_UNIT, DEFAULT_LOCALE, FALLBACK_LEXICON, FALLBACK_LOCALES, LOCALE_LABELS, REALMS, REALM_IDS, SCENE_TEXT_ARROW_SAFE_INSET, SCENE_TEXT_FONTS, SCENE_TEXT_FONT_DEFAULT_ID, SUPPORTED_LOCALES, buildSceneTextFontStylesheetUrls, chuziAiSceneFontPromptList, defaultPurchaseCredits, ensureSceneTextFontsLoaded, formatCreditPrice, getRealmLexiconOverrides, isContentRating, isSupportedLocale, lexiconForRealm, matchAcceptLanguage, normalizeLocale, priceCentsForCredits, resolveSceneTextFont, sceneTextFontCss, sceneTextFontOptions, setRealmLexiconOverrides, t };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map